import { CLOSED_STATUSES, isBatchPick } from '../models/batch-status';
import { AfterViewInit, Component, ElementRef } from '@angular/core';
import { BatchService } from '../Services/batch.service';
import { CommonModule } from '@angular/common';
import { NgxPrintModule } from 'ngx-print';
import { NgxBarcode6 } from 'ngx-barcode6';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeFormat } from '@zxing/library';
import { ScanmedicationComponent } from '../scanmedication/scanmedication.component';
import { Router, ActivatedRoute } from '@angular/router';
import { ViewChild } from '@angular/core';
import { PicklistService } from '../Services/picklist.service';
import { HostListener } from '@angular/core';

@Component({
  selector: 'app-batches',
  imports: [
    CommonModule,
    NgxPrintModule,
    NgxBarcode6,
    ZXingScannerModule,
    ScanmedicationComponent
  ],
  templateUrl: './batches.component.html',
  styleUrl: './batches.component.css',
  providers: [BatchService, PicklistService],
})
export class BatchesComponent implements AfterViewInit {
  @ViewChild('barcodeInput') barcodeInput!: ElementRef<HTMLInputElement>;
  @ViewChild(ScanmedicationComponent)
  scanMedicationComp!: ScanmedicationComponent;
  storeBatchData: any = {};
  numberOfPrescriptions: number = 0;
  numberOfMedications: number = 0;
  medicationsPerPatient: any[] = [];
  groupedMedications: any = {};
  batchData: any = {};
  selectedMedications: string[] = [];
  printMedications: any = [];
  disabled: boolean = false;
  isWrongMedicine: boolean = false;
  medicationIdList: string[] = [];
  makeSummaryDisable: boolean = false;
  cameraPermissionStatus: string = 'Checking permission...';
  cameraGranted: boolean = false;
  batchId: string = '';
  isStartBtnClicked: boolean = true;
  finishedScan: boolean = false;
  isFormDirty = false;
  todayDate: string = '';
  toastMessage = '';
  toastType = '';

  // Navigation guards intentionally removed — scans are persisted server-side
  // on each barcode, so leaving mid-batch is safe and the confirm popup adds
  // friction without preventing data loss.

  isScannerActive: boolean = false;
  isScanSuccessful: boolean = false;
  barcodeFormats = [
    BarcodeFormat.EAN_13,
    BarcodeFormat.UPC_A,
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_128,
    BarcodeFormat.EAN_8,
    BarcodeFormat.ITF,
  ];

  constructor(
    private _batchservice: BatchService,
    private _picklistservice: PicklistService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
  ) {}

  /** A finished batch, or opened from "Bag labels": no scanning, only reprints. */
  get viewOnly(): boolean {
    return this.activatedRoute.snapshot?.queryParamMap?.get('mode') === 'view'
      || CLOSED_STATUSES.includes(this.storeBatchData?.status)
      || this.isBatchPick;
  }

  /** A Batch Pick batch not finished yet: shown here, scanned on the pick and verify screens. */
  get batchPickInProgress(): boolean {
    return this.isBatchPick && !CLOSED_STATUSES.includes(this.storeBatchData?.status);
  }

  get isBatchPick(): boolean {
    return isBatchPick(this.storeBatchData);
  }

  openBatchPick() {
    const status = this.storeBatchData?.status;
    if (status === 'Picked' || status === 'Verifying') {
      this.router.navigate(['/batchpick', this.batchId, 'verify']);
    } else {
      this.router.navigate(['/batchpick', this.batchId, 'pick']);
    }
  }

  ngOnInit(): void {
    this.todayDate = new Date().toLocaleDateString('en-GB');
    this.startScanner();
    this.activatedRoute.paramMap.subscribe(params => {
      this.batchId = params.get('batchId') || '';
      this._batchservice.getBatchById(this.batchId).subscribe((response: any) => {
        this.storeBatchData = response.result;
        if (this.storeBatchData) {
          this.batchData = this.storeBatchData;
          this.numberOfPrescriptions = this.storeBatchData.prescriptions.length;
          this.numberOfMedications = this.storeBatchData.prescriptions.reduce(
            (total: number, prescription: any) => total + prescription.medications.length, 0
          );
          this.medicationList();
          this.getMedicationsList();
          this.printMedicationList();
        }
      });
    });
    setInterval(() => { this.checkCameraPermission(); }, 1000);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.barcodeInput) {
        this.barcodeInput.nativeElement.focus();
        this.barcodeInput.nativeElement.addEventListener('blur', (e: FocusEvent) => {
          // Don't steal focus when the user is interacting with another control
          // (dropdown, button, another input) or when a modal is open.
          const next = e.relatedTarget as HTMLElement | null;
          if (next && next.tagName && /^(SELECT|INPUT|TEXTAREA|BUTTON|A)$/.test(next.tagName)) {
            return;
          }
          setTimeout(() => {
            const active = document.activeElement as HTMLElement | null;
            if (active && /^(SELECT|INPUT|TEXTAREA|BUTTON|A)$/.test(active.tagName) && active !== this.barcodeInput?.nativeElement) {
              return;
            }
            if (!this.scanMedicationComp?.isModalOpen) {
              this.barcodeInput.nativeElement.focus();
            }
          }, 0);
        });
      }
    }, 500);
  }

  showToast(message: string, type: 'success' | 'error' = 'success') {
    this.toastMessage = message;
    this.toastType = type;
    setTimeout(() => { this.toastMessage = ''; }, 3000);
  }

  onInputChange() {
    this.isFormDirty = true;
  }

  checkCameraPermission() {
    navigator.permissions.query({ name: 'camera' as PermissionName })
      .then((permissionStatus) => {
        if (permissionStatus.state === 'granted') {
          this.cameraGranted = true;
          this.cameraPermissionStatus = '';
        } else if (permissionStatus.state === 'prompt') {
          this.cameraPermissionStatus = 'Camera permission needs to be requested.';
          this.cameraGranted = false;
        } else {
          this.cameraPermissionStatus = 'Camera access is denied.';
          this.cameraGranted = false;
        }
      })
      .catch(() => {
        this.cameraPermissionStatus = 'Error checking camera permission.';
      });
  }

  medicationList() {
    if (this.storeBatchData != null) {
      this.batchData = this.storeBatchData;
      this.groupedMedications = this._picklistservice.printPickList(this.batchData);
    }
  }

  startScanner() {
    this.isScannerActive = true;
    this.makeSummaryDisable = true;
    this.activatedRoute.paramMap.subscribe(params => {
      this.batchId = params.get('batchId') || '';
      // Only auto-set InProgress for legacy (non-BatchPick) batches; BatchPick
      // owns its own status lifecycle (Pending → Picking → Picked → Verifying → Completed).
      this._batchservice.getBatchById(this.batchId).subscribe((response: any) => {
        const b = response?.result;
        const isPick = isBatchPick(b);
        if (!isPick && b?.status === 'Pending') {
          this._batchservice.updateBatchStatus(this.batchId, 'InProgress').subscribe();
        }
      });
    });
    this.isStartBtnClicked = true;
    setTimeout(() => {
      if (this.barcodeInput) {
        this.barcodeInput.nativeElement.focus();
      }
    }, 100);
  }

  stopScanner() {
    this.isScannerActive = false;
    this.printMedicationList();
  }

  printPackedList() {
    setTimeout(() => {
      const printContents = document.getElementById('print-medicines')?.innerHTML;
      if (printContents) {
        const printContainer = document.createElement('div');
        printContainer.id = 'print-container';
        printContainer.innerHTML = printContents;
        document.body.appendChild(printContainer);
        const style = document.createElement('style');
        style.innerHTML =
          `@media print {
            body * { display: none !important; }
            #print-container, #print-container * { display: block !important; }
          }`;
        document.head.appendChild(style);
        window.onafterprint = () => {
          if (confirm('Continue scanning current batch, or request a new batch?\n\nOK = New Batch\nCancel = Continue')) {
            this.router.navigate(['/batchlist']);
          }
          document.body.removeChild(printContainer);
          document.head.removeChild(style);
        };
        window.print();
      }
    }, 500);
  }

  printMedicationList() {
    this.printMedications = [];
    this.batchData.prescriptions.forEach((prescription: any) => {
      prescription.medications.forEach((medication: any) => {
        if (medication.isAvailable === true) {
          this.printMedications.push({
            medicationName: medication.medicationName,
            medicineDosage: medication.dosage,
            instruction: medication.instruction,
            medicationId: medication.medicationId,
          });
        }
      });
    });
  }

  onScanResult(result: string) {
    if (result) {
      try {
        this.scanMedicationComp.onBarcodeScanned(result);
        this.printMedicationList();
      } catch {
        this.showToast('Failed to scan the medicine. Try again.', 'error');
      }
    }
  }

  get objectKeys(): (obj: any) => string[] {
    return Object.keys;
  }

  getMedicationsList() {
    this.batchData.prescriptions.forEach((prescription: any) => {
      prescription.medications.forEach((medication: any) => {
        this.medicationIdList.push(medication.medicationId);
      });
    });
    return (this.medicationIdList = [...new Set(this.medicationIdList)]);
  }

  isScanFinished(status: boolean) {
    this.finishedScan = status;
  }

  canDeactivate(): boolean {
    return true;
  }

  refocusBarcode() {
    setTimeout(() => {
      this.barcodeInput?.nativeElement?.focus();
    }, 50);
  }

  handleBarcode(inputElement: HTMLInputElement) {
    if (!this.isScannerActive) return;
    const scannedValue = inputElement.value.trim();
    if (scannedValue) {
      this.onScanResult(scannedValue);
      inputElement.value = '';
    }
  }

  getLastChars(id: string): string {
    if (!id) return '';
    return id.slice(-8);
  }

  triggerPrintPickList() {
    const printContents = document.getElementById('printlist')?.innerHTML;
    if (!printContents) return;

    const popupWin = window.open('', '_blank', 'width=800,height=600');
    if (popupWin) {
      popupWin.document.open();
      popupWin.document.write(`
<html>
  <head>
    <title>Batch Pick List</title>
    <style>
      body { font-family: "Segoe UI", Inter, Roboto, -apple-system, BlinkMacSystemFont, Arial, sans-serif; padding: 16px 18px; margin: 0; }
      .print-container { width: 100%; }
      .print-top-bar { display: flex; justify-content: space-between; align-items: center; }
      .print-date { font-size: 13px; color: #555; font-weight: 500; }
      .print-logos { display: flex; align-items: center; gap: 14px; }
      .print-logo { height: 34px; }
      hr { border: none; border-top: 0.8px solid #e0e0e0; margin: 4px 0 10px 0; }
      .print-title { margin: 6px 0 10px 0; font-size: 19px; font-weight: 700; }
      table { width: 100%; border-collapse: collapse; }
      thead th { text-align: left; padding: 5px 6px; font-weight: 700; font-size: 14px; border-bottom: 1px solid #e0e0e0; }
      tbody td { padding: 5px 6px; text-align: left; font-size: 13.5px; border-bottom: 1px solid #e0e0e0; }
      tbody tr:last-child td { border-bottom: none; }
      .col-stock { width: 48%; }
      .col-prescription { width: 27%; }
      .col-qty { width: 10%; }
      .col-picked { width: 15%; }
    </style>
  </head>
  <body onload="window.print(); window.close();">
    ${printContents}
  </body>
</html>`);
      popupWin.document.close();
    }
  }
}
